// @ts-nocheck
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { Send, RotateCcw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import useAssistantStore from '@/store/assistantStore';
import { getSuggestions } from './suggestions';

const DOCS_BASE_URL = 'https://docs.staty.io';

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
        )}
      >
        {message.content}
      </div>
      {!isUser && message.sources?.length > 0 && (
        <div className="text-xs text-muted-foreground max-w-[85%]">
          From the docs:{' '}
          {message.sources.slice(0, 3).map((s, i) => (
            <React.Fragment key={s.docPath}>
              {i > 0 && ', '}
              <a
                href={`${DOCS_BASE_URL}/${s.docPath}`}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-foreground"
              >
                {s.title}
              </a>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start">
      <div className="bg-muted rounded-lg px-3 py-2.5 flex gap-1">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

export default function HelpDrawer({ league }) {
  const location = useLocation();
  const { isOpen, messages, isLoading, error } = useAssistantStore();
  const close = useAssistantStore((s) => s.close);
  const sendMessage = useAssistantStore((s) => s.sendMessage);
  const retry = useAssistantStore((s) => s.retry);

  const [input, setInput] = React.useState('');
  const scrollRef = React.useRef(null);

  const page = { pathname: location.pathname };
  const orgId = league._id;

  React.useEffect(() => {
    // Keep the newest message in view.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isLoading]);

  const submit = (text) => {
    const value = String(text ?? input).trim();
    if (!value || isLoading) return;
    setInput('');
    sendMessage(orgId, value, page);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const displayName = league.displayName || league.name;

  return (
    <Sheet open={isOpen} onOpenChange={(o) => { if (!o) close(); }} modal={false}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col gap-0 p-0"
        // Non-modal: don't steal focus from the page behind.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="p-4 pb-3 border-b text-left space-y-0.5">
          <SheetTitle className="text-base">Help &amp; AI assistant</SheetTitle>
          <SheetDescription className="text-xs truncate">{displayName}</SheetDescription>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          {messages.length === 0 && (
            <div className="flex flex-col gap-3">
              <div className="bg-muted rounded-lg px-3 py-2 text-sm max-w-[85%]">
                Hi! Ask me anything about running {displayName} on Staty: how features work, or
                live questions about your registrations, teams, and payments.
              </div>
              <div className="flex flex-col items-start gap-2">
                {getSuggestions(location.pathname).map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => submit(q)}
                    className="text-sm border rounded-full px-3 py-1.5 text-left hover:bg-accent transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} />
          ))}
          {isLoading && <TypingIndicator />}
          {error && (
            <div className="flex flex-col items-start gap-2">
              <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-destructive/10 text-destructive">
                {error}
              </div>
              <Button variant="outline" size="sm" onClick={() => retry(orgId, page)}>
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          )}
        </div>

        <div className="border-t p-3 space-y-2">
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask a question…"
              rows={1}
              className="min-h-[38px] max-h-32 resize-none text-sm"
            />
            <Button
              size="icon"
              onClick={() => submit()}
              disabled={!input.trim() || isLoading}
              aria-label="Send"
              className="shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            AI answers may be inaccurate. Verify important details.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
